import { Modal, Button, notification, Tag, Space } from 'antd';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchShopifyProducts, refetchShopifyProduct, setSelectedShopifyProduct, updateShopifyProduct, updateShopifyProductGeneration } from '../../store/shopifyProductsSlice';
import ShopifyProductDetailDescription from './ShopifyPorductDetailDescription';
import { statusApprovedActionColor, statusColorGeneration } from '../../constants/statusColors';
import { GenerationStatus } from '../../constants/status';
import { shopifyProductGeneration } from '../../api/analyzeShopifyPorduct';
import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, RobotOutlined, StopOutlined } from '@ant-design/icons';

interface ShopifyProductDetailModalProps {
    open: boolean;
    handleClose: () => void;
}

const ShopifyProductDetailModal: React.FC<ShopifyProductDetailModalProps> = ({ open, handleClose }) => {

    const dispatch = useAppDispatch()

    const selectedShopifyShop = useAppSelector((state) => state.shopifyShops.selectedShopifyShop)
    const selectedShopifyProduct = useAppSelector((state) => state.shopifyProducts.selectedShopifyProduct)
    const shopifyProducts = useAppSelector((state) => state.shopifyProducts.shopifyProducts)
    const pagination = useAppSelector((state) => state.shopifyProducts.pagination)
    const activeFilters = useAppSelector((state) => state.shopifyProducts.activeFilters)
    const acticeSorter = useAppSelector((state) => state.shopifyProducts.activeSorter)

    const [currentProductIndex, setCurrentProductIndex] = useState<number>(0)
    const [analyzing, setAnalyzing] = useState<boolean>(false)

    useEffect(() => {
        if (open && selectedShopifyProduct) {
            const index = shopifyProducts.findIndex(obj => obj.id === selectedShopifyProduct.id);
            setCurrentProductIndex(index);
        }
    }, [open, shopifyProducts, selectedShopifyProduct]);

    const handleNext = () => {
        const newIndex = Math.min(currentProductIndex + 1, shopifyProducts.length)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedShopifyProduct(shopifyProducts[newIndex]))

        // Check if you can fetch more products
        if (selectedShopifyShop) {
            if (newIndex === shopifyProducts.length - 2 && pagination.lastVisibleDoc && pagination.hasMore) {
                dispatch(
                    fetchShopifyProducts({
                        shopifyShopId: selectedShopifyShop?.id,
                        pageSize: pagination.pageSize,
                        lastVisibleDoc: pagination.lastVisibleDoc,
                        activeSorter: acticeSorter,
                        activeFilters: activeFilters,
                        refetch: false
                    })
                );
            }
        }
    }

    const handleApproveAndNext = (currentProductIndex: number) => {
        if (selectedShopifyProduct && selectedShopifyShop) {
            dispatch(updateShopifyProduct({ id: selectedShopifyProduct.id, shopId: selectedShopifyShop.id, generationStatus: GenerationStatus.APPROVED }));
        }
        if (currentProductIndex < shopifyProducts.length - 1) {
            const newIndex = currentProductIndex + 1
            setCurrentProductIndex(newIndex)
            dispatch(setSelectedShopifyProduct(shopifyProducts[newIndex]))
        }
    }

    const handleUnapprove = (currentProductIndex: number) => {
        if (selectedShopifyProduct && selectedShopifyShop) {
            dispatch(updateShopifyProduct({ id: selectedShopifyProduct.id, shopId: selectedShopifyShop.id, generationStatus: GenerationStatus.NEW }));
        }
    }

    const handleLast = () => {
        const newIndex = Math.max(currentProductIndex - 1, 0)
        setCurrentProductIndex(newIndex)
        dispatch(setSelectedShopifyProduct(shopifyProducts[newIndex]))
    }

    const handleDiscard = (type: "Description" | "Title") => {
        if (selectedShopifyProduct && selectedShopifyShop) {
            setAnalyzing(true)

            const generationUpdate = {
                [type === "Description" ? "descriptionHtml" : "title"]:
                    type === "Description"
                        ? selectedShopifyProduct.descriptionHtml
                        : selectedShopifyProduct.title,
            };

            dispatch(updateShopifyProductGeneration({
                id: selectedShopifyProduct.id,
                shopId: selectedShopifyShop.id,
                generation: generationUpdate
            }))
            setAnalyzing(false)
        }
    }

    const handleGenerateShopifyProduct = async () => {
        console.log("Starting generation for:", selectedShopifyProduct, selectedShopifyShop);

        // Check if required data is available
        if (!selectedShopifyProduct || !selectedShopifyShop) {
            console.error("Product or Shopify shop is not selected.");
            return;
        }

        try {
            setAnalyzing(true);
            console.log("Generating content...");

            // Await the analysis function and capture the result
            await shopifyProductGeneration(selectedShopifyShop.id, selectedShopifyProduct.id);
            dispatch(refetchShopifyProduct({
                shopifyShopId: selectedShopifyShop.id,
                productId: selectedShopifyProduct.id
            }))

        } catch (error) {
            console.error("Error generating content:", error);

            notification.error({
                message: "Content Generation Failed",
            });
        } finally {
            setAnalyzing(false);
        }
    };


    return (
        <Modal
            open={open}
            title={
                <Space size="small">

                    {selectedShopifyProduct?.generationStatus && (
                        <Tag color={statusColorGeneration[selectedShopifyProduct.generationStatus]}>
                            {selectedShopifyProduct.generationStatus.charAt(0).toUpperCase() +
                                selectedShopifyProduct.generationStatus.slice(1)}
                        </Tag>
                    )}
                    <span>{`Shopify Product: ${selectedShopifyProduct?.title || "N/A"}`}</span>
                </Space>
            }
            width={"95vw"}
            height={"85vh"}
            onCancel={handleClose}
            footer={[
                <Button
                    key="generate"
                    type="primary"
                    disabled={analyzing}
                    loading={analyzing}
                    icon={<RobotOutlined />}
                    onClick={handleGenerateShopifyProduct}
                >
                    {analyzing ? "Generating" : "Generate Content"}
                </Button>,
                <Button key="last" type="default" icon={<LeftOutlined />} disabled={currentProductIndex === 0} onClick={handleLast}>
                    Last
                </Button>,
                <Button key="next" type="default" icon={<RightOutlined />} disabled={currentProductIndex === shopifyProducts.length - 1} onClick={handleNext}>
                    Next
                </Button>,
                <Button key="unapprove" type="default" icon={<StopOutlined />} disabled={selectedShopifyProduct?.generationStatus !== GenerationStatus.APPROVED} onClick={() => handleUnapprove(currentProductIndex)}>
                    Unapprove
                </Button>,
                <Button
                    key="approve"
                    type="default"
                    icon={<CheckOutlined />}
                    style={{ backgroundColor: statusApprovedActionColor, color: 'white' }}
                    onClick={() => handleApproveAndNext(currentProductIndex)}
                >
                    {/* // handleApproveAndNext(currentProductIndex)}> */}
                    {currentProductIndex === shopifyProducts.length - 1 ? "Approve" : "Approve & Next"}
                </Button>,
                <Button key="close" type="default" icon={<CloseOutlined />} onClick={handleClose}>
                    Close
                </Button>,
            ]}
            styles={{ body: { height: "75vh", overflow: "hidden" } }}
        >
            <div style={{
                maxHeight: "72vh", // Ensure it doesn't exceed modal height
                overflowY: "auto", // Enable vertical scrolling if content exceeds height
                padding: "16px", // Add padding for better readability
            }}>
                <ShopifyProductDetailDescription shopifyProduct={selectedShopifyProduct} handleDiscard={handleDiscard} />
            </div>
        </Modal>
    );
};

export default ShopifyProductDetailModal;
